/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { resolveMessagingStatus } from '@/lib/messagingStatus'
import {
  useManualMessagingPaidMutation,
  useManualMessagingSuspendMutation,
} from '@/hooks/usePlatformOwnerDetail'

export function MessagingSubscriptionCard({
  owner,
}: {
  owner: {
    id: string
    messagingPlan?: 'MONTHLY' | 'YEARLY' | null
    messagingStartedAt?: string | null
    messagingNextRenewalAt?: string | null
    messagingSuspendedAt?: string | null
  }
}) {
  const status = useMemo(() => resolveMessagingStatus(owner), [owner])

  const markPaidMut = useManualMessagingPaidMutation(owner.id)
  const suspendMut = useManualMessagingSuspendMutation(owner.id)

  const [plan, setPlan] = useState<'MONTHLY' | 'YEARLY'>(
    owner.messagingPlan ?? 'MONTHLY'
  )
  const [paidAt, setPaidAt] = useState('')
  const [amountCfa, setAmountCfa] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    setPlan(owner.messagingPlan ?? 'MONTHLY')
  }, [owner.messagingPlan])

  const onMarkPaid = async () => {
    try {
      const parsedAmount =
        amountCfa.trim() === ''
          ? undefined
          : Math.max(0, Math.floor(Number(amountCfa)))

      if (amountCfa.trim() !== '' && !Number.isFinite(parsedAmount as number)) {
        toast.error('Montant invalide')
        return
      }

      await markPaidMut.mutateAsync({
        plan,
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        amountCfa: parsedAmount,
        note: note.trim() || undefined,
      })

      toast.success('Abonnement messaging activé / prolongé')
      setPaidAt('')
      setAmountCfa('')
      setNote('')
    } catch (e: any) {
      toast.error('Activation messaging impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const onSuspend = async () => {
    try {
      await suspendMut.mutateAsync({
        reason: reason.trim() || undefined,
      })
      toast.success('Messaging suspendu')
      setReason('')
    } catch (e: any) {
      toast.error('Suspension impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const badgeVariant =
    status === 'ACTIVE'
      ? 'default'
      : status === 'EXPIRED'
      ? 'secondary'
      : 'destructive'

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-semibold'>Messaging subscription</h3>
            <p className='text-sm text-muted-foreground'>
              Activation / suspension de SMS et WhatsApp
            </p>
          </div>
          <Badge variant={badgeVariant}>{status}</Badge>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        <div className='grid gap-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Plan</span>
            <span>{owner.messagingPlan ?? '—'}</span>
          </div>
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Started</span>
            <span>{owner.messagingStartedAt ?? '—'}</span>
          </div>
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Next renewal</span>
            <span>{owner.messagingNextRenewalAt ?? '—'}</span>
          </div>
          <div className='flex justify-between'>
            <span className='text-muted-foreground'>Suspended at</span>
            <span>{owner.messagingSuspendedAt ?? '—'}</span>
          </div>
        </div>

        <div className='rounded-lg border p-3 space-y-3'>
          <div className='font-medium text-sm'>Activer / prolonger</div>

          <div className='flex gap-2'>
            <Button
              type='button'
              variant={plan === 'MONTHLY' ? 'default' : 'outline'}
              onClick={() => setPlan('MONTHLY')}
            >
              Mensuel
            </Button>
            <Button
              type='button'
              variant={plan === 'YEARLY' ? 'default' : 'outline'}
              onClick={() => setPlan('YEARLY')}
            >
              Annuel
            </Button>
          </div>

          <Input
            type='number'
            min={0}
            step={1}
            value={amountCfa}
            onChange={(e) => setAmountCfa(e.target.value)}
            placeholder='Montant CFA (optionnel)'
          />

          <Input
            type='datetime-local'
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />

          <Input
            placeholder='Note interne (optionnel)'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className='flex justify-end'>
            <Button onClick={onMarkPaid} disabled={markPaidMut.isPending}>
              {markPaidMut.isPending ? 'Traitement…' : 'Activer / prolonger'}
            </Button>
          </div>
        </div>

        <div className='rounded-lg border p-3 space-y-3'>
          <div className='font-medium text-sm'>Suspendre messaging</div>

          <Input
            placeholder='Raison (optionnel)'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <div className='flex justify-end'>
            <Button
              variant='destructive'
              onClick={onSuspend}
              disabled={suspendMut.isPending}
            >
              {suspendMut.isPending ? 'Suspension…' : 'Suspendre'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
